import Icon from '@atoms/Icon/Icon';
import { ISharePanel } from '@models/ISharePanel';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import { useEffect, useState } from 'react';

interface Props extends ISharePanel {
  readonly hasWhatsapp?: boolean;
}

const SharePanel = ({
  title,
  tooltipText = '',
  hasWhatsapp = false,
  shareOrigin = 'article_share',
}: Props) => {
  const [isCopySuccessModalOpen, setIsCopySuccessModalOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const {
    copyToClipboardAriaLabel,
    shareToFacebookAriaLabel,
    shareToXAriaLabel,
    shareToWhatsappAriaLabel,
  } = useAriaLabels();

  const socialShareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      `${pageUrl}?utm_source=facebook&utm_medium=${shareOrigin}`
    )}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      `${pageUrl}?utm_source=twitter&utm_medium=${shareOrigin}`
    )}`,
    whatsapp: `whatsapp://send?text=${encodeURIComponent(
      `${pageUrl}?utm_source=whatsapp&utm_medium=${shareOrigin}`
    )}`,
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(
        `${url}?utm_source=url&utm_medium=${shareOrigin}`
      );

      setIsCopySuccessModalOpen((isOpen) => !isOpen);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPageUrl(window.location.href);
    }
  }, []);

  return (
    <>
      <div className="text-black-100 dark:text-white">
        {title && <p className="body-two">{title}</p>}

        <div className="mt-4 flex gap-5">
          <button
            type="button"
            onClick={() => copyToClipboard(pageUrl)}
            aria-label={copyToClipboardAriaLabel}
          >
            <Icon
              name="share"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </button>

          <a
            href={socialShareLinks.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label={shareToFacebookAriaLabel}
          >
            <Icon
              name="facebook"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </a>

          <a
            href={socialShareLinks.twitter}
            target="_blank"
            rel="noreferrer"
            aria-label={shareToXAriaLabel}
          >
            <Icon
              name="twitter"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </a>

          {hasWhatsapp && (
            <a
              href={socialShareLinks.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label={shareToWhatsappAriaLabel}
            >
              <Icon
                name="whatsapp"
                className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
                size={IconSizeEnum.lg}
              />
            </a>
          )}
        </div>
      </div>

      <InfoModal
        type={InfoTypeEnum.Success}
        isModalOpen={isCopySuccessModalOpen}
        toggleModal={setIsCopySuccessModalOpen}
        title={tooltipText}
      />
    </>
  );
};

export default SharePanel;
